import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, FlaskConical, Cpu, Award, Users, Search,
  AlertTriangle, ClipboardCheck, FileText, Package, FolderOpen,
  TestTube, GitBranch, Plug, UserPlus, Bell, LogOut, Menu,
  ChevronRight, Upload, ExternalLink,
  TestTube2, GitPullRequest, Wrench, GraduationCap, BarChart3,
  UserCog, FileBarChart, Settings2,
  BookMarked, ClipboardPen, FileBadge2, Activity, LineChart,
  FlaskRound, ShieldAlert,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { useNotifications } from '@/context/NotificationContext';
import NotificationPanel from '@/components/ui/NotificationPanel';
import SearchPalette from '@/components/ui/SearchPalette';
import ChatbotWidget from '@/components/ui/ChatbotWidget';
import api from '@/lib/api';
import { format, differenceInDays } from 'date-fns';

// ── Design tokens (hardcoded — matches Forge Quantum Solution exactly) ────────
const SB_BG      = '#0D1117';
const SB_BG2     = '#161B22';
const GOLD       = '#b07d1a';
const GOLD_L     = '#c9922a';
const GOLD_FAINT = 'rgba(176,125,26,0.12)';

const navGroups = [
  {
    label: 'Core Intelligence',
    items: [
      { path: '/dashboard',     icon: LayoutDashboard, label: 'Dashboard'       },
      { path: '/registry',      icon: FlaskConical,    label: 'Lab Registry'    },
      { path: '/equipment',     icon: Cpu,             label: 'Equipment'       },
      { path: '/certifications',icon: Award,           label: 'Certifications'  },
      { path: '/partners',      icon: Users,           label: 'Partners'        },
      { path: '/finder',        icon: Search,          label: 'Lab Finder'      },
    ],
  },
  {
    label: 'Quality Management',
    items: [
      { path: '/capa',            icon: AlertTriangle,  label: 'CAPA / Deviations'      },
      { path: '/audits',          icon: ClipboardCheck, label: 'Audit Tracker'          },
      { path: '/audit-trail',     icon: ShieldAlert,    label: 'Audit Trail (21 CFR)'   },
      { path: '/sop',             icon: FileText,       label: 'SOP Management'         },
      { path: '/bmr',             icon: Package,        label: 'Batch Records'          },
      { path: '/training',        icon: GraduationCap,  label: 'Training & Competency'  },
      { path: '/quality-metrics', icon: BarChart3,      label: 'Quality Metrics'        },
    ],
  },
  {
    label: 'LIMS',
    items: [
      { path: '/lims/samples',        icon: TestTube2,    label: 'Samples'             },
      { path: '/lims/specifications',  icon: BookMarked,   label: 'Specifications'      },
      { path: '/lims/worksheets',      icon: ClipboardPen, label: 'Test Worksheets'     },
      { path: '/lims/coa',             icon: FileBadge2,   label: 'Certificate of Analysis' },
      { path: '/lims/stability',       icon: Activity,     label: 'Stability Studies'   },
      { path: '/lims/qc-charts',       icon: LineChart,    label: 'QC Control Charts'   },
      { path: '/lims/inventory',        icon: FlaskRound,   label: 'Inventory'           },
    ],
  },
  {
    label: 'Lab Operations',
    items: [
      { path: '/dms',            icon: FolderOpen,     label: 'Documents'       },
      { path: '/requests',       icon: TestTube,       label: 'Test Requests'   },
      { path: '/workflows',      icon: GitBranch,      label: 'Workflows'       },
      { path: '/samples',        icon: TestTube2,      label: 'Sample Tracking' },
      { path: '/change-control', icon: GitPullRequest, label: 'Change Control'  },
      { path: '/calibration-pm', icon: Wrench,         label: 'Calibration & PM'},
    ],
  },
  {
    label: 'System',
    items: [
      { path: '/integrations', icon: Plug,      label: 'Integrations'    },
      { path: '/onboarding',   icon: UserPlus,  label: 'Onboard Partner' },
      { path: '/bulk-import',  icon: Upload,    label: 'Bulk Import'     },
    ],
  },
  {
    label: 'Administration',
    items: [
      { path: '/user-management', icon: UserCog,     label: 'User Management' },
      { path: '/report-builder',  icon: FileBarChart, label: 'Report Builder'  },
      { path: '/settings',        icon: Settings2,    label: 'Settings'        },
    ],
  },
];

// ── Alert chip types ─────────────────────────────────────────────
type ChipKey = 'expiry' | 'capa' | 'gmp';

export default function Layout() {
  const { user, logout } = useAuth();
  const { unreadCount }  = useNotifications();
  const location         = useLocation();
  const navigate         = useNavigate();
  const [notifOpen,   setNotifOpen]   = useState(false);
  const [searchOpen,  setSearchOpen]  = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeChip,  setActiveChip]  = useState<ChipKey | null>(null);
  const chipRef = useRef<HTMLDivElement>(null);

  // ── Mobile detection ─────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [isScrolled, setIsScrolled] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  // Auto-close sidebar on mobile when route changes
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  // Live data for alert chips
  const { data: certHealth } = useQuery({
    queryKey: ['dashboard', 'cert-health', {}],
    queryFn: () => api.get('/dashboard/cert-health').then((r) => r.data.data),
    staleTime: 120000,
  });
  const { data: openCapas } = useQuery({
    queryKey: ['topbar-open-capas'],
    queryFn: () => api.get('/capa', { params: { status: 'open', limit: 5 } }).then((r) => r.data),
    staleTime: 120000,
  });
  const { data: recentAudits } = useQuery({
    queryKey: ['topbar-audits'],
    queryFn: () => api.get('/audits', { params: { tab: 'completed', limit: 5 } }).then((r) => r.data),
    staleTime: 120000,
  });

  const expiryCount = (certHealth?.renewalDue ?? 0) + (certHealth?.expired ?? 0);
  const capaCount   = openCapas?.meta?.total ?? 0;
  const gmpPassed   = (recentAudits?.data ?? []).filter((a: { score: number }) => a.score >= 80).length;

  const chips = [
    {
      key: 'expiry' as ChipKey,
      label: expiryCount > 0 ? `${expiryCount} Expiry Alert${expiryCount > 1 ? 's' : ''}` : 'Certs OK',
      color: expiryCount > 0 ? '#DC2626' : '#16A34A',
      bg:    expiryCount > 0 ? '#FEF2F2' : '#F0FDF4',
      route: '/certifications',
    },
    {
      key: 'capa' as ChipKey,
      label: capaCount > 0 ? `${capaCount} Open CAPA${capaCount > 1 ? 's' : ''}` : 'No Open CAPAs',
      color: capaCount > 0 ? '#D97706' : '#16A34A',
      bg:    capaCount > 0 ? '#FFFBEB' : '#F0FDF4',
      route: '/capa',
    },
    {
      key: 'gmp' as ChipKey,
      label: gmpPassed > 0 ? 'GMP Compliant' : 'GMP Review Needed',
      color: gmpPassed > 0 ? '#16A34A' : '#D97706',
      bg:    gmpPassed > 0 ? '#F0FDF4' : '#FFFBEB',
      route: '/audits',
    },
  ];

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(true); }
      if (e.key === 'Escape') { setNotifOpen(false); setSearchOpen(false); setActiveChip(null); }
    };
    const clickOut = (e: MouseEvent) => {
      if (chipRef.current && !chipRef.current.contains(e.target as Node)) setActiveChip(null);
    };
    window.addEventListener('keydown', handler);
    window.addEventListener('mousedown', clickOut);
    return () => { window.removeEventListener('keydown', handler); window.removeEventListener('mousedown', clickOut); };
  }, []);

  const currentPage = navGroups
    .flatMap((g) => g.items)
    .find((i) => location.pathname === i.path)?.label || 'Dashboard';

  const initials = (user?.name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #fdf9f4 100%)' }}>

      {/* ─── MOBILE OVERLAY ──────────────────────────────────────── */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 39 }}
        />
      )}

      {/* ─── SIDEBAR ─────────────────────────────────────────────── */}
      <aside
        className="flex flex-col flex-shrink-0 z-20 transition-[width] duration-300"
        style={{
          width:    sidebarOpen ? '280px' : '0',
          minWidth: sidebarOpen ? '280px' : '0',
          background: SB_BG,
          borderRight: `1px solid rgba(255,255,255,0.05)`,
          overflow: sidebarOpen ? 'visible' : 'hidden',
          ...(isMobile && sidebarOpen
            ? { position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 40 }
            : {}),
        }}
      >
        {/* ── Brand ── */}
        <div style={{
          padding: '18px 16px 14px',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          flexShrink: 0,
        }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: '1.4rem', lineHeight: 1.1, color: '#fff', whiteSpace: 'nowrap', letterSpacing: '0.02em' }}>
            Quantum <span style={{ color: '#c9922a' }}>Kairoz</span>
          </div>
        </div>

        {/* ── Nav ── */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
          {navGroups.map((group) => (
            <div key={group.label} style={{ padding: '10px 8px 4px' }}>
              <div style={{
                fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '1.5px', color: 'rgba(255,255,255,0.22)',
                fontFamily: '"DM Mono", monospace',
                padding: '0 10px', marginBottom: 6,
              }}>
                {group.label}
              </div>

              {group.items.map((item) => (
                <NavLink key={item.path} to={item.path}>
                  {({ isActive }) => (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 9,
                      padding: '7px 10px',
                      borderRadius: 2,
                      marginBottom: 1,
                      cursor: 'pointer',
                      position: 'relative',
                      background: isActive ? GOLD_FAINT : 'transparent',
                      color: isActive ? GOLD_L : 'rgba(255,255,255,0.42)',
                      borderLeft: isActive ? `3px solid ${GOLD}` : '3px solid transparent',
                      transition: 'color 0.2s, background 0.2s',
                      fontSize: '0.82rem',
                      fontWeight: 400,
                      letterSpacing: '0.04em',
                    }}
                    onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#c9922a'; }}}
                    onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.42)'; }}}
                    >
                      <item.icon size={15} style={{ flexShrink: 0 }} />
                      <span style={{ flex: 1, whiteSpace: 'nowrap' }}>{item.label}</span>
                      {item.path === '/capa' && unreadCount > 0 && (
                        <span style={{
                          fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
                          background: '#D97706', color: '#fff',
                        }}>
                          {unreadCount}
                        </span>
                      )}
                      {isActive && <ChevronRight size={11} style={{ opacity: 0.5 }} />}
                    </div>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* ── User footer ── */}
        <div style={{ flexShrink: 0, padding: 12, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 9,
            padding: '9px 10px', borderRadius: 2, marginBottom: 8,
            background: SB_BG2,
            border: '1px solid rgba(255,255,255,0.06)',
          }}>
            <div style={{
              width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
              background: `linear-gradient(135deg, ${GOLD}, #a07218)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, color: '#fff',
            }}>
              {initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 400, color: 'rgba(255,255,255,0.65)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.name || 'User'}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.28)', textTransform: 'capitalize', marginTop: 1 }}>
                {(user?.role || '').replace('_', ' ')}
              </div>
            </div>
            <button
              onClick={logout}
              title="Sign out"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.28)', padding: 4, display: 'flex', alignItems: 'center' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.65)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.28)')}
            >
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      {/* ─── MAIN AREA ───────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* ── Topbar — matches Forge Quantum Navbar scroll behavior ── */}
        <header style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: isScrolled ? '0 20px' : '0 20px',
          height: isScrolled ? 50 : 56,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          transition: 'all 0.4s ease',
          background: isScrolled ? 'rgba(255,255,255,0.95)' : 'transparent',
          backdropFilter: isScrolled ? 'blur(16px)' : 'none',
          WebkitBackdropFilter: isScrolled ? 'blur(16px)' : 'none',
          borderBottom: isScrolled ? '1px solid rgba(0,0,0,0.08)' : '1px solid transparent',
          flexShrink: 0,
          boxShadow: isScrolled ? '0 4px 30px rgba(0,0,0,0.06)' : 'none',
        }}>
          {/* hamburger — hover turns gold like Forge nav links */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5a5a6e', padding: '4px', display: 'flex', alignItems: 'center', borderRadius: 2, transition: 'color 0.2s' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#b07d1a')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#5a5a6e')}
          >
            <Menu size={18} />
          </button>

          {/* page title — Forge uses Cormorant Garamond 1.4rem/700 for brand */}
          <h1 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 700,
            fontSize: '1.4rem',
            letterSpacing: '0.02em',
            color: '#1a1a2e',
            flex: 1,
            margin: 0,
            lineHeight: 1.1,
          }}>
            {currentPage}
          </h1>

          {/* alert chips + popovers */}
          <div ref={chipRef} style={{ display: 'flex', gap: 6, alignItems: 'center', position: 'relative' }}>
            {chips.map((chip) => (
              <div key={chip.key} style={{ position: 'relative' }}>
                <button
                  onClick={() => setActiveChip(activeChip === chip.key ? null : chip.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '4px 10px', borderRadius: 100,
                    background: chip.bg,
                    border: `1px solid ${chip.color}30`,
                    fontSize: 11, fontWeight: 600, color: chip.color,
                    whiteSpace: 'nowrap', cursor: 'pointer',
                    transition: 'filter 0.15s',
                    outline: activeChip === chip.key ? `2px solid ${chip.color}60` : 'none',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.filter = 'brightness(0.93)')}
                  onMouseLeave={(e) => (e.currentTarget.style.filter = 'none')}
                >
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: chip.color, flexShrink: 0 }} />
                  {chip.label}
                </button>

                {/* Popover card */}
                <AnimatePresence>
                  {activeChip === chip.key && (
                    <motion.div
                      initial={{ opacity: 0, y: 6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 4, scale: 0.97 }}
                      transition={{ duration: 0.15 }}
                      style={{
                        position: 'absolute', top: 'calc(100% + 8px)', left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#fff', borderRadius: 4,
                        boxShadow: '0 8px 32px rgba(15,23,42,0.14), 0 2px 8px rgba(15,23,42,0.08)',
                        border: '1px solid rgba(0,0,0,0.1)',
                        width: 280, zIndex: 9999, overflow: 'hidden',
                      }}
                    >
                      {/* Card header */}
                      <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: chip.color }}>{chip.label}</span>
                        <button
                          onClick={() => { navigate(chip.route); setActiveChip(null); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#5a5a6e', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 6, transition: 'background 0.1s' }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.04)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                        >
                          View all <ExternalLink size={10} />
                        </button>
                      </div>

                      {/* Card body */}
                      <div style={{ padding: '8px 6px', maxHeight: 240, overflowY: 'auto' }}>
                        {chip.key === 'expiry' && (
                          <>
                            {certHealth?.expired > 0 && (
                              <div style={{ padding: '6px 8px', marginBottom: 4, borderRadius: 8, background: '#FEF2F2' }}>
                                <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#DC2626' }}>{certHealth.expired} Expired</p>
                                <p style={{ margin: '2px 0 0', fontSize: 11, color: '#5a5a6e' }}>Immediate renewal required</p>
                              </div>
                            )}
                            {certHealth?.renewalDue > 0 && (
                              <div style={{ padding: '6px 8px', borderRadius: 8, background: '#FFFBEB' }}>
                                <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#D97706' }}>{certHealth.renewalDue} Due for Renewal</p>
                                <p style={{ margin: '2px 0 0', fontSize: 11, color: '#5a5a6e' }}>Renewal due within 30 days</p>
                              </div>
                            )}
                            {expiryCount === 0 && (
                              <p style={{ margin: '8px', fontSize: 12, color: '#5a5a6e' }}>All certifications are valid.</p>
                            )}
                          </>
                        )}

                        {chip.key === 'capa' && (
                          <>
                            {(openCapas?.data ?? []).length === 0
                              ? <p style={{ margin: '8px', fontSize: 12, color: '#5a5a6e' }}>No open CAPAs.</p>
                              : (openCapas?.data ?? []).map((c: { id: string; capaCode: string; title: string; severity: string; dueDate: string }) => {
                                  const days = differenceInDays(new Date(c.dueDate), new Date());
                                  const urgColor = days < 0 ? '#DC2626' : days <= 7 ? '#D97706' : '#5a5a6e';
                                  return (
                                    <button key={c.id}
                                      onClick={() => { navigate('/capa'); setActiveChip(null); }}
                                      style={{ width: '100%', textAlign: 'left', padding: '7px 8px', borderRadius: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 2, transition: 'background 0.1s' }}
                                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f7f5f2')}
                                      onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#1a1a2e', fontFamily: 'monospace' }}>{c.capaCode}</span>
                                        <span style={{ fontSize: 10, fontWeight: 600, color: urgColor }}>{days < 0 ? `${Math.abs(days)}d overdue` : `${days}d left`}</span>
                                      </div>
                                      <p style={{ margin: '2px 0 0', fontSize: 11, color: '#5a5a6e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</p>
                                      <span style={{ fontSize: 10, color: '#94A3B8', textTransform: 'capitalize' }}>{c.severity}</span>
                                    </button>
                                  );
                                })
                            }
                          </>
                        )}

                        {chip.key === 'gmp' && (
                          <>
                            {(recentAudits?.data ?? []).length === 0
                              ? <p style={{ margin: '8px', fontSize: 12, color: '#5a5a6e' }}>No recent audits found.</p>
                              : (recentAudits?.data ?? []).map((a: { id: string; auditCode: string; type: string; score: number; scheduledDate: string }) => (
                                  <button key={a.id}
                                    onClick={() => { navigate('/audits'); setActiveChip(null); }}
                                    style={{ width: '100%', textAlign: 'left', padding: '7px 8px', borderRadius: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 2, transition: 'background 0.1s' }}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f7f5f2')}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <span style={{ fontSize: 11, fontWeight: 700, color: '#1a1a2e', fontFamily: 'monospace' }}>{a.auditCode}</span>
                                      <span style={{ fontSize: 11, fontWeight: 700, color: a.score >= 80 ? '#16A34A' : a.score >= 60 ? '#D97706' : '#DC2626' }}>{a.score}%</span>
                                    </div>
                                    <p style={{ margin: '2px 0 0', fontSize: 11, color: '#5a5a6e', textTransform: 'capitalize' }}>{a.type} · {format(new Date(a.scheduledDate), 'd MMM yyyy')}</p>
                                  </button>
                                ))
                            }
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* search */}
          <button
            onClick={() => setSearchOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '0.55rem 1.3rem', borderRadius: 2,
              background: 'none', border: '1px solid rgba(0,0,0,0.1)',
              color: '#5a5a6e', fontSize: '0.82rem', cursor: 'pointer',
              transition: 'all 0.2s', letterSpacing: '0.06em',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#b07d1a'; e.currentTarget.style.color = '#b07d1a'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(0,0,0,0.1)'; e.currentTarget.style.color = '#5a5a6e'; }}
          >
            <Search size={13} />
            <span>Search</span>
            <kbd style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 2, color: '#5a5a6e' }}>⌘K</kbd>
          </button>

          {/* notification bell */}
          <button
            data-testid="notification-bell"
            onClick={() => setNotifOpen(!notifOpen)}
            style={{
              position: 'relative', width: 36, height: 36,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 2, background: 'none', border: 'none', cursor: 'pointer',
              color: '#5a5a6e', transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#b07d1a')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#5a5a6e')}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: 6, right: 6,
                width: 16, height: 16, borderRadius: '50%',
                background: '#DC2626', color: '#fff',
                fontSize: 9, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* avatar */}
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: `linear-gradient(135deg, ${GOLD}, #a07218)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, color: '#fff', cursor: 'pointer',
            flexShrink: 0,
          }} title={user?.name}>
            {initials}
          </div>
        </header>

        {/* ── Page content ── */}
        <main
          ref={mainRef}
          onScroll={(e) => setIsScrolled((e.target as HTMLElement).scrollTop > 60)}
          style={{ flex: 1, overflowY: 'auto', padding: '24px', background: 'linear-gradient(160deg, #ffffff 40%, #f7f5f2 100%)' }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* ─── Overlays ───────────────────────────────────────────── */}
      <AnimatePresence>
        {notifOpen  && <NotificationPanel onClose={() => setNotifOpen(false)} />}
      </AnimatePresence>
      <AnimatePresence>
        {searchOpen && <SearchPalette onClose={() => setSearchOpen(false)} />}
      </AnimatePresence>

      {/* ─── Chatbot ─────────────────────────────────────────────── */}
      <ChatbotWidget />
    </div>
  );
}
